import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Card, Icon, PageEyebrow } from '../../ui/index.js';
import { useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { getAlbumKey } from '../albums/albumKeys.js';
import { MediaGrid } from '../viewer/MediaGrid.js';
import { encryptRating } from './ratingCrypto.js';
import { useDecryptedRatings } from './useDecryptedRatings.js';

interface Resolved {
  albumId: string;
  rootId: string;
  albumKey: CryptoKey;
}

// Owner-only cross-album top-rated gallery. Drains every photo under the
// root via /roots/{rootId}/photos paginated, decrypts the rating per page,
// and re-sorts the array on every page event so the highest-rated photos
// surface as soon as they're discovered. The hook is the single source of
// truth for rows + loading state; the page is a thin wrapper around it.
export function TopRatedPage() {
  const { albumId } = useParams<{ albumId: string }>();
  const session = useAuthSession();
  const { metadata } = useInfra();
  const [resolved, setResolved] = useState<Resolved | { error: string } | null>(null);

  useEffect(() => {
    if (!albumId) return;
    let cancelled = false;
    setResolved(null);
    void (async () => {
      try {
        const { albums } = await metadata.listAlbums(session.jwt);
        const self = albums.find((a) => a.id === albumId);
        if (!self) {
          if (!cancelled) setResolved({ error: 'album non trovato' });
          return;
        }
        const root = albums.find((a) => a.id === self.rootAlbumId);
        if (!root || !root.wrappedAlbumKey) {
          if (!cancelled) setResolved({ error: 'chiave radice mancante' });
          return;
        }
        const albumKey = await getAlbumKey(root.rootAlbumId, root.wrappedAlbumKey, session.rsaPriv);
        if (cancelled) return;
        setResolved({ albumId, rootId: root.rootAlbumId, albumKey });
      } catch (err) {
        if (!cancelled) setResolved({ error: err instanceof Error ? err.message : 'errore' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [albumId, session.jwt, session.rsaPriv, metadata]);

  if (!albumId) return null;
  if (resolved === null) {
    return <p className="py-6 text-sm text-on-surface-variant">Carico…</p>;
  }
  if ('error' in resolved) {
    return <p className="py-6 text-sm text-error">Errore: {resolved.error}</p>;
  }

  return (
    <TopRatedView
      backTo={`/albums/${encodeURIComponent(resolved.albumId)}`}
      backLabel="Album"
      rootId={resolved.rootId}
      albumKey={resolved.albumKey}
      eyebrow="classifica"
      editable
    />
  );
}

interface TopRatedViewProps {
  rootId: string;
  albumKey: CryptoKey;
  backTo: string;
  backLabel: string;
  eyebrow: string;
  /** When true, the fullscreen viewer renders editable stars and a change
   *  hits POST /photos/rating-batch. Owner-only path; the viewer-mode share
   *  page leaves this off so the API doesn't get called and the UI doesn't
   *  invite an action that would 403. */
  editable?: boolean;
}

// Shared rendering surface — TopRatedPage and SharedTopRatedPage compose it
// after resolving K_album from their respective sources (listAlbums for owner,
// listIncomingShares for viewer).
export function TopRatedView({
  rootId,
  albumKey,
  backTo,
  backLabel,
  eyebrow,
  editable = false,
}: TopRatedViewProps) {
  const session = useAuthSession();
  const { metadata } = useInfra();
  const { rows, loading, hasMore, totalLoaded, error, loadMore, setRowRating } =
    useDecryptedRatings(rootId, albumKey, session.jwt);

  // Owner-only inline rating edit. Optimistic mutation + rollback mirrors
  // AlbumPhotosSection.handleRate; the main difference is that a rating of 0
  // (cleared) drops the photo from the gallery entirely, since the page only
  // shows rated photos. A keyGen conflict (concurrent rotation) rolls back
  // and prompts the user — we don't auto-reload because the page is mid-drain
  // and a forced refresh would lose the pages already loaded.
  async function handleRate(photoId: string, rating: number): Promise<void> {
    const row = rows.find((r) => r.photo.id === photoId);
    if (!row) return;
    const next = rating === 0 ? null : rating;
    const rollback = setRowRating(photoId, next);
    try {
      const ciphertext = next === null ? undefined : await encryptRating(next, albumKey);
      const resp = await metadata.ratePhotosBatch(
        {
          rootAlbumId: row.photo.rootAlbumId,
          items: [
            {
              albumId: row.photo.albumId,
              photoId: row.photo.id,
              keyGen: row.photo.keyGen ?? 1,
              ...(ciphertext ? { encryptedRating: ciphertext } : {}),
            },
          ],
        },
        session.jwt,
      );
      if (resp.conflicts.includes(photoId)) {
        rollback();
        window.alert(
          'Aggiornamento rifiutato: la chiave dell’album è cambiata. Ricarica la pagina e riprova.',
        );
      }
    } catch (err) {
      rollback();
      window.alert(err instanceof Error ? err.message : 'Errore aggiornamento valutazione');
    }
  }

  const summary =
    totalLoaded === 0 && loading
      ? 'Cerco le foto valutate…'
      : totalLoaded === 0
        ? hasMore
          ? 'Nessuna foto valutata nelle pagine già scansionate. Continua a cercare.'
          : 'Nessuna foto valutata in questa radice.'
        : totalLoaded === 1
          ? '1 foto valutata'
          : `${totalLoaded} foto valutate`;

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col gap-3">
        <PageEyebrow>{eyebrow}</PageEyebrow>
        <Breadcrumb backTo={backTo} backLabel={backLabel} />
        <div className="flex items-center gap-3">
          <Icon name="star" filled className="text-2xl text-warning" />
          <h2 className="font-display text-2xl font-semibold">Più valutate</h2>
        </div>
        <p className="text-sm text-on-surface-variant">
          {summary}
          {loading ? ' · in corso…' : hasMore ? ' · ce ne sono altre da scoprire' : null}
        </p>
        {error ? <p className="text-xs text-error">Errore: {error}</p> : null}
      </div>

      {rows.length === 0 && !loading && !hasMore ? (
        <Card className="flex flex-col items-center gap-2 py-12 text-center">
          <Icon name="star_border" className="text-4xl text-on-surface-variant" />
          <p className="text-sm text-on-surface-variant">
            Apri una foto e tocca le stelle per iniziare a valutare.
          </p>
        </Card>
      ) : (
        <MediaGrid
          items={rows.map((r) => ({
            id: r.photo.id,
            url: r.url,
            filename: r.filename,
            ...(r.mimeType ? { mimeType: r.mimeType } : {}),
            rating: r.rating,
            info: {
              encryptedMeta: r.photo.encryptedMeta,
              uploadedAt: r.photo.createdAt,
              encryptedSize: r.photo.size,
              ...(r.photo.keyGen !== undefined ? { keyGen: r.photo.keyGen } : {}),
            },
          }))}
          albumKey={albumKey}
          {...(editable ? { onRate: handleRate } : {})}
        />
      )}

      {hasMore ? (
        <div className="flex flex-col items-center gap-1">
          <Button
            variant="outline"
            onClick={loadMore}
            loading={loading}
            disabled={loading}
            leftIcon={<Icon name="expand_more" />}
          >
            Carica altre
          </Button>
          <p className="text-xs text-on-surface-variant">
            Cerco fra le foto della radice una pagina alla volta — quelle non valutate restano sul
            server.
          </p>
        </div>
      ) : null}
    </div>
  );
}

function Breadcrumb({ backTo, backLabel }: { backTo: string; backLabel: string }) {
  return (
    <nav className="flex flex-wrap items-center gap-2 text-sm">
      <Link to={backTo} className="text-on-surface-variant transition-colors hover:text-cyan">
        {backLabel}
      </Link>
      <Icon name="chevron_right" className="text-base text-on-surface-variant" />
      <span className="font-display text-base font-semibold text-cyan">Più valutate</span>
    </nav>
  );
}
